import React, { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Input, Popup, SelectInput } from 'src/components'
import { UpdateBrincoForm, updateSchemaBrinco } from 'src/@types/validations/BirthCertificate'
import { EarringDto } from 'src/dto/birth-certificate.dto'
import { useUpdateEarring } from 'src/hooks/api/birth-certificate/mutations/updateEarring'
import { toast } from 'react-toastify'
import { toastSyncSuccess } from 'src/utils/syncToast'
import { useStyles } from './styles'

interface UpdateEarringModalProps {
  isOpen: boolean
  earring: EarringDto | null
  onClose: () => void
  onSuccess: () => void
}

const selectStatusOptions = [
  { label: 'Usado', value: 'Usado' },
  { label: 'Disponível', value: 'disponivel' }
]

export const UpdateEarringModal: React.FC<UpdateEarringModalProps> = ({ isOpen, earring, onClose, onSuccess }) => {
  const styles = useStyles()
  const { mutateAsync: updateEarring, isPending } = useUpdateEarring()
  const formRef = useRef<HTMLFormElement>(null)

  const {
    setValue,
    watch,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<UpdateBrincoForm>({
    resolver: yupResolver(updateSchemaBrinco)
  })

  useEffect(() => {
    if (isOpen && earring) {
      reset({
        idBrinco: earring.idBrinco,
        idFazenda: earring.idFazenda,
        tipo: earring.tipo ?? undefined,
        data: earring.data ?? undefined,
        numeroCompleto: earring.numeroCompleto,
        numeroSufixo: earring.numeroSufixo,
        status: earring.status,
        acao: earring.acao ?? undefined,
        idFicha: earring.idFicha ?? undefined,
        prefixo: earring.prefixo ?? undefined,
        sufixo: earring.sufixo ?? undefined,
        ordem: earring.ordem ?? undefined,
        id: earring.id ?? undefined
      })
    }
  }, [isOpen, earring, reset])

  const buildNumeroSufixo = (prefixo: string, sufixo: string) => {
    setValue('numeroSufixo', `${prefixo}${sufixo}`)
  }

  const onSubmit = async (data: UpdateBrincoForm) => {
    try {
      await updateEarring(data)
      toastSyncSuccess('Brinco atualizado com sucesso!')
      onSuccess()
      onClose()
    } catch {
      toast.error('Erro ao atualizar brinco.')
    }
  }

  return (
    <Popup
      title='Editar Brinco'
      isOpen={isOpen}
      onClose={onClose}
      closeOnClickOutside={false}
      footer={() => (
        <div className={styles.modalFooter}>
          <Button type='button' outlined onClick={onClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            type='submit'
            isLoading={isPending}
            disabled={isPending}
            onClick={() => formRef.current?.requestSubmit()}
          >
            Salvar
          </Button>
        </div>
      )}
    >
      <form ref={formRef} className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <Input
          label='Brinco'
          value={watch('numeroCompleto') || ''}
          error={errors?.numeroCompleto?.message}
          onChange={(value: string) => setValue('numeroCompleto', value)}
        />

        <SelectInput
          label='Status'
          showBy={['label']}
          onChange={({ value }) => setValue('status', value)}
          selectBy='value'
          options={selectStatusOptions}
          value={watch('status') || ''}
          error={errors?.status?.message}
        />
        <Input label='RGN' value={watch('numeroSufixo') || ''} disabled />
        <Input
          label='Prefixo'
          value={watch('prefixo') || ''}
          onChange={(value: string) => {
            setValue('prefixo', value)
            buildNumeroSufixo(value, watch('sufixo') || '')
          }}
        />
        <Input
          label='Sufixo'
          value={watch('sufixo') || ''}
          onChange={(value: string) => {
            setValue('sufixo', value)
            buildNumeroSufixo(watch('prefixo') || '', value)
          }}
        />
      </form>
    </Popup>
  )
}
